/**
 * Utility functions for internationalization
 */

import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import i18n from '../i18n';

/**
 * Hook for internationalization utilities
 * @returns {Object} - Object with i18n utility functions
 */
export const useI18nUtils = () => {
    const { i18n } = useTranslation();

    /**
     * Extracts localized value from object structure like {name: "Meals", namePl: "Posiłki"}
     * @param {Object} obj - Object containing localized values
     * @returns {string} - Localized value based on current language
     */
    const getI18FromName = useCallback((obj) => {
        if (!obj || typeof obj !== 'object') {
            return '';
        }

        // If Polish language, try to get Polish value first, fallback to default
        if (i18n.language === 'pl') {
            return obj.namePl || obj.name || '';
        }

        // For other languages, use default field
        return obj.name || '';
    }, [i18n.language]);

    /**
     * Maps array of objects to localized options for dropdowns
     * @param {Array} items - Array of objects with localized values
     * @returns {Array} - Array of objects with label property
     */
    const mapToI18Options = useCallback((items) => {
        if (!Array.isArray(items)) {
            return [];
        }

        return items.map(item => ({
            ...item,
            label: getI18FromName(item)
        }));
    }, [getI18FromName]);

    return {
        getI18FromName,
        mapToI18Options
    };
};

/**
 * Legacy function for backward compatibility
 * @param {Object} obj - Object containing localized values
 * @param {string} language - Language code (optional)
 * @returns {string} - Localized value based on current language
 */
export const getI18FromName = (obj, language = null) => {
    if (!obj || typeof obj !== 'object') {
        return '';
    }

    // Get current language from parameter or i18n instance, fallback to 'en'
    const currentLanguage = language || i18n.language || 'en';

    // If Polish language, try to get Polish value first, fallback to default
    if (currentLanguage === 'pl') {
        return obj.namePl || obj.name || '';
    }

    // For other languages, use default field
    return obj.name || '';
};

/**
 * Maps array of objects to localized options for dropdowns
 * @param {Array} items - Array of objects with localized values
 * @returns {Array} - Array of objects with label property
 */
export const mapToI18Options = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return items.map(item => ({
        ...item,
        label: getI18FromName(item)
    }));
};

// Example usage:
// const obj = { name: "Meals", namePl: "Posiłki" };
// const value = getLocalizedValue(obj); // Returns "Posiłki" if language is 'pl', "Meals" otherwise
